import React, { useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { INK, FONT_MONO, STATUS, BRAND } from 'Utils/styleTokens';

// Daily buckets for the trailing N days, oldest first.
const buildDays = (days) => {
  const out = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    out.push(d);
  }
  return out;
};

const dayLabel = (d) => d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
const timeLabel = (ts) => {
  const t = Date.parse(ts);
  return Number.isFinite(t)
    ? new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : '';
};

// Status pill palette for the drilldown table. A failure is NEUTRAL here, not red: the chart
// reports activity, and a job that failed is not an alert on this page — it is one of the runs
// that happened. Red was reading as "the landscape is on fire" for ordinary retried scans.
const statusPill = (status, isError) => {
  const s = String(status || '').toUpperCase();
  if (isError || ['FAILED', 'ERROR', 'TIMEOUT', 'INTERRUPTED'].includes(s)) {
    return { color: INK[500], bg: INK[100] };
  }
  if (['SUCCEEDED', 'COMPLETED', 'MASKED'].includes(s)) {
    return { color: STATUS.success.text, bg: STATUS.success.bg };
  }
  return { color: STATUS.info.text, bg: STATUS.info.bg };
};

/**
 * "Activity — last 30 days" stacked daily bar chart (mockups 2a / 5a / 3a).
 * Pure presentational: callers map their job lists to `events`.
 *
 * Failures are NOT a series of their own. A failed run is counted in its own family's bar (a
 * failed discovery is still a discovery) and is called out only in the day's drilldown, in
 * neutral ink. An `error` key left over in `events` is therefore counted but never stacked —
 * the bars only ever draw the keys `series` declares.
 *
 * @param title     card heading (uppercase eyebrow style)
 * @param series    [{ key, label, color }] — stack order and legend
 * @param events    [{ ts, key, ...detail }] — ts is anything Date.parse accepts. When
 *                  `drilldown` is on, each event may also carry { id, kind, name, status,
 *                  to, error } to render a clickable row.
 * @param days      window length, default 30
 * @param drilldown when true, clicking a bar opens a table of that day's runs/errors
 */
function ActivityChart30d({ title, series, events, days = 30, drilldown = false }) {
  const history = useHistory();
  const [selectedIdx, setSelectedIdx] = useState(null);

  const buckets = useMemo(() => {
    const dayList = buildDays(days);
    const startTs = dayList[0].getTime();
    const counts = dayList.map(() => ({}));
    const dayEvents = dayList.map(() => []);
    (events || []).forEach((event) => {
      const t = Date.parse(event.ts);
      if (!Number.isFinite(t) || t < startTs) return;
      const idx = Math.min(dayList.length - 1, Math.floor((t - startTs) / 86400000));
      counts[idx][event.key] = (counts[idx][event.key] || 0) + 1;
      dayEvents[idx].push(event);
    });
    return dayList.map((d, i) => ({ date: d, counts: counts[i], events: dayEvents[i] }));
  }, [events, days]);

  // Selection can fall out of range if the window shifts (e.g. across a 30s refresh).
  const selected = selectedIdx != null && buckets[selectedIdx] ? buckets[selectedIdx] : null;

  // Bar height (and the click gate) count only the declared series, so an undeclared key in
  // `events` — an `error` event from a caller that hasn't been updated — never scales the axis
  // against bars it isn't drawn in.
  const stackTotal = (bucket) => series.reduce((a, s) => a + (bucket.counts[s.key] || 0), 0);

  const maxTotal = Math.max(1, ...buckets.map(stackTotal));

  const seriesColor = (key) => (series.find((s) => s.key === key) || {}).color || INK[400];

  const onBarClick = (i, hasEvents) => {
    if (!drilldown || !hasEvents) return;
    setSelectedIdx((prev) => (prev === i ? null : i));
  };

  return (
    <Card className='mb-3'>
      <Card.Body>
        <div className='d-flex align-items-center mb-2'>
          <h5 className='text-left sg-eyebrow mb-0'>{title}</h5>
          <div className='ml-auto d-flex' style={{ gap: 12 }}>
            {series.map((s) => (
              <span key={s.key} className='sg-small d-inline-flex align-items-center' style={{ gap: 5 }}>
                <span style={{ width: 9, height: 9, background: s.color, borderRadius: 2, flex: 'none' }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>
        <div
          className='d-flex align-items-end'
          style={{ height: 80, gap: 3, borderBottom: `1px solid ${INK[150]}` }}
        >
          {buckets.map((bucket, i) => {
            const parts = series
              .filter((s) => bucket.counts[s.key])
              .map((s) => `${bucket.counts[s.key]} ${s.label.toLowerCase()}`);
            const clickable = drilldown && bucket.events.length > 0;
            return (
              <div
                key={i}
                title={`${dayLabel(bucket.date)} — ${parts.join(', ') || 'no activity'}${clickable ? ' (click for detail)' : ''}`}
                onClick={() => onBarClick(i, bucket.events.length > 0)}
                className='d-flex flex-column-reverse'
                style={{
                  flex: 1,
                  height: '100%',
                  justifyContent: 'flex-start',
                  cursor: clickable ? 'pointer' : 'default',
                  outline: selectedIdx === i ? `2px solid ${INK[400]}` : 'none',
                  outlineOffset: 1,
                  borderRadius: 1,
                }}
              >
                {series.map((s) =>
                  bucket.counts[s.key] ? (
                    <div
                      key={s.key}
                      style={{
                        background: s.color,
                        height: `${(bucket.counts[s.key] / maxTotal) * 100}%`,
                        minHeight: 3,
                        borderRadius: 1,
                      }}
                    />
                  ) : null
                )}
              </div>
            );
          })}
        </div>
        <div className='d-flex' style={{ gap: 3 }}>
          {buckets.map((bucket, i) => (
            <div
              key={i}
              className='text-center'
              style={{ flex: 1, fontFamily: FONT_MONO, fontSize: 11, color: 'var(--ink-900)', whiteSpace: 'nowrap' }}
            >
              {i % 7 === 0 ? dayLabel(bucket.date) : ''}
            </div>
          ))}
        </div>

        {selected && (
          <div
            className='mt-3'
            style={{ border: `1px solid ${INK[150]}`, borderRadius: 'var(--r-md)', padding: 12 }}
          >
            <div className='d-flex align-items-center mb-2' style={{ gap: 8 }}>
              <strong>Activity on {dayLabel(selected.date)}</strong>
              <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
                {selected.events.length} run{selected.events.length === 1 ? '' : 's'}
              </span>
              <button
                type='button'
                onClick={() => setSelectedIdx(null)}
                className='ml-auto'
                aria-label='Close'
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', fontSize: 16, lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            <div style={{ maxHeight: 220, overflow: 'auto' }}>
              <table className='w-100' style={{ fontSize: 13 }}>
                <tbody>
                  {selected.events
                    .slice()
                    .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts))
                    .map((ev, idx) => {
                      const isError = ev.key === 'error' || !!ev.error;
                      const pill = statusPill(ev.status, isError);
                      const clickable = !!ev.to;
                      return (
                        <tr
                          key={ev.id || idx}
                          onClick={clickable ? () => history.push(ev.to) : undefined}
                          style={{
                            borderTop: `1px solid ${INK[100]}`,
                            cursor: clickable ? 'pointer' : 'default',
                          }}
                        >
                          <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                            <span
                              style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                borderRadius: 2,
                                background: seriesColor(ev.key),
                                marginRight: 7,
                              }}
                            />
                            {ev.kind || ev.key}
                          </td>
                          <td style={{ padding: '6px 8px', fontFamily: FONT_MONO, color: clickable ? BRAND.blue : 'inherit' }}>
                            {ev.name || '—'}
                            {ev.error && (
                              <div
                                className='sg-small'
                                style={{ color: INK[500], fontFamily: 'inherit' }}
                                title={String(ev.error)}
                              >
                                {String(ev.error).slice(0, 120)}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <span
                              className='sg-pill sg-pill--nodot'
                              style={{ background: pill.bg, color: pill.color }}
                              title={isError && ev.error ? String(ev.error) : undefined}
                            >
                              {isError ? (ev.status || 'ERROR') : (ev.status || '—')}
                            </span>
                          </td>
                          <td style={{ padding: '6px 8px', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
                            {timeLabel(ev.ts)}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default ActivityChart30d;
